import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import Icon from '../ui/Icon';
import { MonsterThumb, monsterImgSrc } from '../ui/ZoneMonsterPicker';
import { fmtKamas } from './auctionUtils';

const heroStyle = (monster) => (monster?.img ? { '--auc-img': `url("${monsterImgSrc(monster.img)}")` } : undefined);

// Ordre d'affichage du podium : 2e, 1er, 3e
const PODIUM_ORDER = [1, 0, 2];

/**
 * Résultats d'une enchère terminée : palmarès (chiffres clés), podium des 3 plus grosses ventes
 * puis galerie des autres zones vendues. `lots` = lots sérialisés, `monsters` = images par zone.
 */
const AuctionResults = ({ lots, monsters = {} }) => {
    const { t } = useLanguage();
    const sold = lots.filter(l => l.amount > 0).sort((a, b) => b.amount - a.amount);
    if (sold.length === 0) return null;

    const total = sold.reduce((acc, l) => acc + l.amount, 0);
    const buyers = new Map();
    sold.forEach(l => {
        const b = buyers.get(l.bidderId) || { name: l.bidderName, count: 0, total: 0 };
        b.count++; b.total += l.amount;
        buyers.set(l.bidderId, b);
    });
    const topBuyer = [...buyers.values()].sort((a, b) => b.total - a.total)[0];
    const mostWanted = [...sold].sort((a, b) => b.bidCount - a.bidCount || b.amount - a.amount)[0];
    const podium = sold.slice(0, 3);
    const rest = sold.slice(3);

    return (
        <div className="auc-res">
            <div className="auc-res__stats">
                <div className="auc-res__stat auc-res__stat--main">
                    <Icon name="gem" size={20} />
                    <span>{t('auctionResults.total', 'Kamas récoltés')}</span>
                    <strong>{fmtKamas(total)}</strong>
                </div>
                <div className="auc-res__stat">
                    <Icon name="map" size={20} />
                    <span>{t('auctionResults.sold', 'Zones vendues')}</span>
                    <strong>{sold.length} / {lots.length}</strong>
                </div>
                <div className="auc-res__stat">
                    <Icon name="trophy" size={20} />
                    <span>{t('auctionResults.topBuyer', 'Plus gros acheteur')}</span>
                    <strong>{topBuyer.name}</strong>
                    <small>{t('auctionResults.buyerDetail', '{n} zone(s) · {amount}', { n: topBuyer.count, amount: fmtKamas(topBuyer.total) })}</small>
                </div>
                <div className="auc-res__stat">
                    <Icon name="swords" size={20} />
                    <span>{t('auctionResults.mostWanted', 'Zone la plus disputée')}</span>
                    <strong>{mostWanted.zone}</strong>
                    <small>{t('auctionResults.bidsCount', '{n} mise(s)', { n: mostWanted.bidCount })}</small>
                </div>
            </div>

            <ol className="auc-podium" aria-label={t('auctionResults.podium', 'Podium des plus grosses ventes')}>
                {PODIUM_ORDER.filter(i => podium[i]).map(i => {
                    const l = podium[i];
                    const monster = monsters[l.zone];
                    return (
                        <li key={l._id} className={`auc-podium__step auc-podium__step--${i + 1}${l.isLeader ? ' is-mine' : ''}`}>
                            <div className="auc-podium__card" style={heroStyle(monster)}>
                                <MonsterThumb monster={monster} zone={l.zone} size={i === 0 ? 96 : 76} className="auc-podium__monster" />
                                <strong className="auc-podium__zone">{l.zone}</strong>
                                <span className="auc-podium__buyer">{l.bidderName}</span>
                                <span className="auc-podium__amount">{fmtKamas(l.amount)}</span>
                            </div>
                            <div className="auc-podium__base"><span>{i + 1}</span></div>
                        </li>
                    );
                })}
            </ol>

            {rest.length > 0 && (
                <ul className="auc-gallery">
                    {rest.map((l, idx) => {
                        const monster = monsters[l.zone];
                        return (
                            <li key={l._id} className={`auc-gallery__item${l.isLeader ? ' is-mine' : ''}`} style={heroStyle(monster)}>
                                <span className="auc-gallery__rank">#{idx + 4}</span>
                                <MonsterThumb monster={monster} zone={l.zone} size={48} />
                                <span className="auc-gallery__text">
                                    <strong>{l.zone}</strong>
                                    <span>{l.bidderName}</span>
                                </span>
                                <span className="auc-amount">{fmtKamas(l.amount)}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default AuctionResults;
